// The composer's working copy of what a broadcast delivers, and the pure
// projections onto the create / update requests. Owned by the create and
// edit forms; `BroadcastEditorFields` renders it.
//
// Platform broadcasts pick a channel: an email (subject, rich body,
// preheader, reply-to) or a Direct message (plain text from the Lean In
// account). Both channels' working copies are kept, so flipping the Channel
// control back and forth loses nothing; only the active channel's content is
// ever submitted. Group broadcasts are always email.

import type { BroadcastEmailSendVia, BroadcastSendVia } from '@leanin/shared/broadcast-enums';

import type { BroadcastComposeContent } from '@/lib/mutations/broadcast-create-plan';
import type { UpdateBroadcastInput } from '@/lib/mutations/admin';
import type { AdminBroadcastDetail, BroadcastDoc } from '@/lib/queries/broadcasts';

export type BroadcastChannel = 'email' | 'dm';

export const BROADCAST_CHANNELS: readonly BroadcastChannel[] = ['email', 'dm'];

export interface EmailContentDraft {
  readonly subject: string;
  readonly preheaderText: string;
  readonly bodyDoc: BroadcastDoc;
  readonly replyTo: string;
}

export interface BroadcastContentDraft {
  readonly channel: BroadcastChannel;
  readonly email: EmailContentDraft;
  readonly dm: { readonly bodyText: string };
}

// The send-via an email composed in the platform composer uses; the
// recipient count and the create request agree on it.
export const PLATFORM_EMAIL_SEND_VIA: BroadcastEmailSendVia = 'email';

export function sendViaForChannel(channel: BroadcastChannel): BroadcastSendVia {
  return channel === 'dm' ? 'dm' : PLATFORM_EMAIL_SEND_VIA;
}

export function newContentDraft(email: EmailContentDraft): BroadcastContentDraft {
  return { channel: 'email', email, dm: { bodyText: '' } };
}

// Seeds the edit form from a stored broadcast. A Direct message row's
// subject is derived server-side from its text, so the email copy starts
// blank rather than echoing it.
export function contentDraftFromDetail(
  detail: AdminBroadcastDetail,
  emptyDoc: () => BroadcastDoc,
): BroadcastContentDraft {
  if (detail.sendVia === 'dm') {
    return {
      channel: 'dm',
      email: { subject: '', preheaderText: '', bodyDoc: emptyDoc(), replyTo: '' },
      dm: { bodyText: detail.bodyText },
    };
  }
  return newContentDraft({
    subject: detail.subject,
    preheaderText: detail.preheaderText ?? '',
    bodyDoc: detail.bodyDoc,
    replyTo: detail.replyTo ?? '',
  });
}

// Anything typed in either channel — what the unsaved-changes guard protects.
export function contentDraftHasInput(draft: BroadcastContentDraft): boolean {
  const { email, dm } = draft;
  return (
    email.subject.trim().length > 0 ||
    email.preheaderText.trim().length > 0 ||
    email.replyTo.trim().length > 0 ||
    !docIsEmpty(email.bodyDoc) ||
    dm.bodyText.trim().length > 0
  );
}

// What the active channel still needs before it can be saved or sent.
export type ContentDraftGap = 'subject' | 'message';

export function contentDraftGap(draft: BroadcastContentDraft): ContentDraftGap | null {
  if (draft.channel === 'dm') {
    return draft.dm.bodyText.trim().length === 0 ? 'message' : null;
  }
  if (draft.email.subject.trim().length === 0) return 'subject';
  if (docIsEmpty(draft.email.bodyDoc)) return 'message';
  return null;
}

// The create request's content. Callers check `contentDraftGap` first.
export function composeContentFromDraft(
  draft: BroadcastContentDraft,
  emailSendVia: BroadcastEmailSendVia,
): BroadcastComposeContent {
  if (draft.channel === 'dm') return { sendVia: 'dm', bodyText: draft.dm.bodyText };
  return {
    sendVia: emailSendVia,
    subject: draft.email.subject.trim(),
    bodyDoc: draft.email.bodyDoc,
    preheaderText: blankToNull(draft.email.preheaderText),
    replyTo: blankToNull(draft.email.replyTo),
  };
}

// The update request's content fields. `sendVia` rides along only when the
// channel changes, so editing an email draft never rewrites its stored
// email channel.
export function contentPatchFromDraft(
  draft: BroadcastContentDraft,
  storedSendVia: BroadcastSendVia,
): Pick<UpdateBroadcastInput, 'sendVia' | 'subject' | 'bodyDoc' | 'preheaderText' | 'replyTo' | 'bodyText'> {
  if (draft.channel === 'dm') return { sendVia: 'dm', bodyText: draft.dm.bodyText };
  return {
    ...(storedSendVia === 'dm' ? { sendVia: PLATFORM_EMAIL_SEND_VIA } : {}),
    subject: draft.email.subject.trim(),
    bodyDoc: draft.email.bodyDoc,
    preheaderText: blankToNull(draft.email.preheaderText),
    replyTo: blankToNull(draft.email.replyTo),
  };
}

// Replaces the selection [start, end) of `text` with `insert`; returns the
// new text and the caret position just after the insertion.
export function insertAtSelection(
  text: string,
  insert: string,
  start: number,
  end: number,
): { readonly text: string; readonly caret: number } {
  return { text: text.slice(0, start) + insert + text.slice(end), caret: start + insert.length };
}

export function docIsEmpty(doc: BroadcastDoc): boolean {
  const blocks = doc.content as ReadonlyArray<{
    content?: ReadonlyArray<{ type?: string }>;
  }>;
  if (blocks.length === 0) return true;
  // A lone hardBreak (Shift+Enter on an empty line) carries no content —
  // same notion as the server's docToText, which projects it to whitespace.
  return blocks.every((block) => {
    const inline = block.content;
    return inline === undefined || inline.every((node) => node.type === 'hardBreak');
  });
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
